import { z } from "zod";
import { FINANCIAL_FIELDS } from "./access";
import { ACTIVITY_ACTION, resolveCurrency, type AccessModule } from "./enums";
import { isoDate, isoDateNullable, uuid, type ErrorCode } from "./schemas/common";
import {
  createBudgetItemSchema, createChecklistItemSchema, createGuestSchema, createNoteSchema, createRegistryItemSchema,
  createSeatingTableSchema, createShoppingItemSchema, createTimelineEventSchema, createVendorSchema,
  inspirationPhotoMetaSchema, updateBudgetItemSchema, updateChecklistItemSchema, updateGuestSchema,
  updateInspirationPhotoSchema, updateNoteSchema, updateRegistryItemSchema, updateSeatingTableSchema,
  updateShoppingItemSchema, updateTimelineEventSchema, updateVendorSchema,
} from "./schemas/modules";
import {
  categoryBudgetsSchema, createWeddingSchema, updateMemberSchema, updateWeddingSchema, type MemberDTO, type WeddingSummaryDTO,
} from "./schemas/wedding";

/*
 * Server sync (Apple ↔ D1). The wire format is the web DTO: web field names, ISO-8601 dates, Apple enum raw values,
 * money as numbers (null when masked), ids as lowercase UUID strings. Files (inspiration image, vendor contract) never
 * travel in sync JSON — they go through the file routes. Apple mirrors every value below in Swift; a change here is a
 * change there in the same piece of work (docs/architecture/cross-platform-contract.md ▸ "Server sync").
 */

/* ───────────────────────── Entities ───────────────────────── */

/** Everything the sync endpoints carry. Order is the documented order, not a sync order. */
export const SYNC_ENTITY = [
  "wedding", "guest", "budgetItem", "checklistItem", "shoppingItem", "note",
  "inspirationPhoto", "vendor", "timelineEvent", "registryItem", "seatingTable", "activityLog", "membership",
] as const;
export type SyncEntity = (typeof SYNC_ENTITY)[number];

/** The ten planner sections: per-module grants, free-tier caps and `ModuleService` apply. */
export const SYNC_MODULE_ENTITY = [
  "guest", "budgetItem", "checklistItem", "shoppingItem", "note",
  "inspirationPhoto", "vendor", "timelineEvent", "registryItem", "seatingTable",
] as const satisfies readonly SyncEntity[];
export type SyncModuleEntity = (typeof SYNC_MODULE_ENTITY)[number];

/** Pulled, never pushed: the server writes the activity log for server-backed weddings. */
export const SYNC_PULL_ONLY_ENTITIES = ["activityLog"] as const satisfies readonly SyncEntity[];

/**
 * Pushed only as `upsert` of an existing row, only by the wedding owner, only with `syncMembershipPatchSchema`
 * (role / grants / seeFinancials / revoked). Never imported, never created or deleted through sync: collaborators
 * join through (seat-bound) invites. The server enforces this; the schema only describes it.
 */
export const SYNC_OWNER_UPDATE_ONLY_ENTITIES = ["membership"] as const satisfies readonly SyncEntity[];

/** What `POST /weddings/:weddingId/sync/push` accepts (`SYNC_ENTITY` minus the pull-only ones). */
export const SYNC_PUSH_ENTITY = ["wedding", ...SYNC_MODULE_ENTITY, "membership"] as const;
export type SyncPushEntity = (typeof SYNC_PUSH_ENTITY)[number];

/** What `POST /sync/import` rows may carry. Never `wedding` (it has its own key) and never `membership`. */
export const SYNC_IMPORT_ENTITY = [...SYNC_MODULE_ENTITY, "activityLog"] as const;
export type SyncImportEntity = (typeof SYNC_IMPORT_ENTITY)[number];

export const SYNC_PUSH_OP = ["upsert", "delete"] as const;
export type SyncPushOp = (typeof SYNC_PUSH_OP)[number];

/**
 * Ops the server accepts per pushed entity; any other pair is rejected per change with `UNSUPPORTED_ENTITY`.
 * A wedding is created by import or `POST /weddings` and deleted only by `DELETE /weddings/:weddingId` — never by a
 * sync batch, so a device bug can never soft-delete a whole wedding. An `inspirationPhoto` row is created only by its
 * image upload (file route, the device's id); an `upsert` of a photo id the server doesn't have is answered
 * `UNSUPPORTED_ENTITY` (held, sent again after the upload), never `NOT_FOUND` (which would revert the local photo).
 */
export const SYNC_ENTITY_OPS: Readonly<Record<SyncPushEntity, readonly SyncPushOp[]>> = {
  wedding: ["upsert"],
  guest: SYNC_PUSH_OP, budgetItem: SYNC_PUSH_OP, checklistItem: SYNC_PUSH_OP, shoppingItem: SYNC_PUSH_OP, note: SYNC_PUSH_OP,
  inspirationPhoto: SYNC_PUSH_OP, vendor: SYNC_PUSH_OP, timelineEvent: SYNC_PUSH_OP, registryItem: SYNC_PUSH_OP,
  seatingTable: SYNC_PUSH_OP,
  membership: ["upsert"],
};

/** D1 table per entity (apps/api/src/db/schema.ts). Every one carries `server_seq`. */
export const SYNC_ENTITY_TABLE: Readonly<Record<SyncEntity, string>> = {
  wedding: "weddings",
  guest: "guests",
  budgetItem: "budget_items",
  checklistItem: "checklist_items",
  shoppingItem: "shopping_items",
  note: "notes",
  inspirationPhoto: "inspiration_photos",
  vendor: "vendors",
  timelineEvent: "timeline_events",
  registryItem: "registry_items",
  seatingTable: "seating_tables",
  activityLog: "activity_log",
  membership: "memberships",
};

/** Access module (grants, caps via `featureForModule`) of each planner-section entity — the ten `ModuleService`s. */
export const SYNC_ENTITY_MODULE: Readonly<Partial<Record<SyncEntity, AccessModule>>> = {
  guest: "guests",
  budgetItem: "budget",
  checklistItem: "checklist",
  shoppingItem: "shopping",
  note: "notes",
  inspirationPhoto: "inspiration",
  vendor: "vendors",
  timelineEvent: "timeline",
  registryItem: "registry",
  seatingTable: "seating",
};

/**
 * The view grant a pull checks before sending an entity's rows (`canView(access, module)`), same as the REST reads:
 * the ten sections plus `activityLog` → "activity" (GET /activity). `wedding` and `membership` have no module gate,
 * like GET /weddings/:weddingId and GET /members (whose e-mail masking still applies).
 */
export const SYNC_ENTITY_VIEW_MODULE: Readonly<Partial<Record<SyncEntity, AccessModule>>> = {
  ...SYNC_ENTITY_MODULE,
  activityLog: "activity",
};

/** CloudKit / SwiftData record type per entity (apps/apple/Bridewell/Sync/RecordMapping.swift ▸ `CKRecordType`). */
export const SYNC_ENTITY_APPLE_RECORD_TYPE: Readonly<Record<SyncEntity, string>> = {
  wedding: "Wedding",
  guest: "Guest",
  budgetItem: "BudgetItem",
  checklistItem: "ChecklistItem",
  shoppingItem: "ShoppingItem",
  note: "WedNote",
  inspirationPhoto: "InspirationPhoto",
  vendor: "Vendor",
  timelineEvent: "TimelineEvent",
  registryItem: "RegistryItem",
  seatingTable: "SeatingTable",
  activityLog: "ActivityLogEntry",
  membership: "CollaboratorAccess",
};

/**
 * Money fields per entity, derived from `FINANCIAL_FIELDS`. A pull for a member without `canSeeFinancials` carries
 * them as `null`: the device keeps its local value (never writes 0) and never pushes these keys — a push that does
 * is rejected `FORBIDDEN`, exactly like the web PATCH.
 */
export const SYNC_ENTITY_MONEY_FIELDS: Readonly<Partial<Record<SyncEntity, readonly string[]>>> = {
  wedding: FINANCIAL_FIELDS.overview ?? [],
  budgetItem: FINANCIAL_FIELDS.budget ?? [],
  shoppingItem: FINANCIAL_FIELDS.shopping ?? [],
  vendor: FINANCIAL_FIELDS.vendors ?? [],
  registryItem: FINANCIAL_FIELDS.registry ?? [],
};

/* ───────────────────────── Limits, backends, identities ───────────────────────── */

export const SYNC_LIMITS = {
  /** Rows per pull page when the device sends no `limit`. */
  pullDefault: 500,
  pullMax: 1000,
  /** Changes per push batch. */
  pushMax: 200,
  /** Rows per import chunk. */
  importChunkMax: 200,
  /** Request body ceiling for push and import (1 MiB). */
  bodyBytes: 1_048_576,
} as const;

/**
 * Where a wedding lives (Apple `Wedding.storageBackend`, a CloudKit field the owner adds in the Console). Absent =
 * "cloudkit" (every wedding created before server sync).
 */
export const WEDDING_STORAGE_BACKEND = ["cloudkit", "server"] as const;
export type WeddingStorageBackend = (typeof WEDDING_STORAGE_BACKEND)[number];

/** Apple `CollaboratorAccess.participantRecordName` of a server-backed member: `"user:" + Bridewell user id`. */
export const MEMBERSHIP_IDENTITY_PREFIX = "user:";

export function membershipIdentity(userId: string): string {
  return MEMBERSHIP_IDENTITY_PREFIX + userId;
}

/** The user id inside a `membershipIdentity`, or null for anything else (an iCloud record name). */
export function parseMembershipIdentity(identity: string): string | null {
  return identity.startsWith(MEMBERSHIP_IDENTITY_PREFIX) && identity.length > MEMBERSHIP_IDENTITY_PREFIX.length
    ? identity.slice(MEMBERSHIP_IDENTITY_PREFIX.length)
    : null;
}

/** Ids are lowercase on the wire. Apple sends `uuidString.lowercased()`; the server normalizes again on receipt. */
export function normalizeSyncId(id: string): string {
  return id.trim().toLowerCase();
}

/** A UUID string as the server accepts it (case-insensitive, surrounding whitespace ignored). */
export const syncIdSchema = z.string().max(64).transform(normalizeSyncId).pipe(uuid);

export function isSyncId(id: string): boolean {
  return syncIdSchema.safeParse(id).success;
}

/** Device-generated idempotency key: sha256 hex or `entity:id:tag`. Case-sensitive, never normalized. */
export const syncChangeIdSchema = z.string().min(8).max(128).regex(/^[A-Za-z0-9:_-]+$/);

/**
 * Fields that hold another row's id (pinned against the module schemas by a test). Lowercase on the wire like row
 * ids; the server runs `normalizeSyncIdFields` before validating, so an upper-case reference never splits a link.
 */
export const SYNC_ENTITY_ID_FIELDS: Readonly<Partial<Record<SyncEntity, readonly string[]>>> = {
  guest: ["seatingTableId", "householdId"],
  budgetItem: ["linkedVendorId"],
  timelineEvent: ["linkedVendorId"],
};

/** A copy of `fields` with the entity's id-valued fields lowercased (non-strings untouched: validation reports them). */
export function normalizeSyncIdFields(entity: SyncEntity, fields: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...fields };
  for (const key of SYNC_ENTITY_ID_FIELDS[entity] ?? []) {
    const value = out[key];
    if (typeof value === "string") out[key] = normalizeSyncId(value);
  }
  return out;
}

/* ───────────────────────── Field schemas ───────────────────────── */

/**
 * The only membership patch a push may carry (owner only, existing non-owner rows only). Strict: `displayName` and
 * anything else is a validation failure, never silently dropped.
 */
export const syncMembershipPatchSchema = z
  .object({
    role: updateMemberSchema.shape.role,
    grants: updateMemberSchema.shape.grants,
    seeFinancials: updateMemberSchema.shape.seeFinancials,
    /** true = revoke (keeps the row), false = restore — `MembershipService.setRevoked`. */
    revoked: z.boolean().optional(),
  })
  .strict();
export type SyncMembershipPatch = z.infer<typeof syncMembershipPatchSchema>;

/** Text clipped instead of rejected: an imported history line is never worth failing a move over. */
const clipped = (max: number) => z.string().catch("").transform((s) => s.trim().slice(0, max));

/**
 * One imported activity entry (Apple `ActivityLogEntry`). Tolerant: unknown keys are stripped, an unknown action
 * becomes "edit" (Apple's own fallback), text is clipped. There is no actor id: the server never takes one from a
 * client and stores `actor_user_id = ""` for imported history.
 */
export const syncActivityImportSchema = z.object({
  actorDisplayName: clipped(80),
  action: z.enum(ACTIVITY_ACTION).catch("edit"),
  entityType: clipped(80),
  entityId: clipped(128),
  summary: clipped(240),
  timestamp: isoDate,
});
export type SyncActivityImport = z.infer<typeof syncActivityImportSchema>;

const SYNC_PATCH_SCHEMAS: Readonly<Record<SyncEntity, z.ZodType>> = {
  wedding: updateWeddingSchema,
  guest: updateGuestSchema,
  budgetItem: updateBudgetItemSchema,
  checklistItem: updateChecklistItemSchema,
  shoppingItem: updateShoppingItemSchema,
  note: updateNoteSchema,
  inspirationPhoto: updateInspirationPhotoSchema,
  vendor: updateVendorSchema,
  timelineEvent: updateTimelineEventSchema,
  registryItem: updateRegistryItemSchema,
  seatingTable: updateSeatingTableSchema,
  activityLog: syncActivityImportSchema,
  membership: syncMembershipPatchSchema,
};

const SYNC_CREATE_SCHEMAS: Readonly<Record<SyncImportEntity, z.ZodType>> = {
  guest: createGuestSchema,
  budgetItem: createBudgetItemSchema,
  checklistItem: createChecklistItemSchema,
  shoppingItem: createShoppingItemSchema,
  note: createNoteSchema,
  inspirationPhoto: inspirationPhotoMetaSchema,
  vendor: createVendorSchema,
  timelineEvent: createTimelineEventSchema,
  registryItem: createRegistryItemSchema,
  seatingTable: createSeatingTableSchema,
  activityLog: syncActivityImportSchema,
};

function own<T>(map: Readonly<Record<string, T>>, key: string, what: string): T {
  if (!Object.prototype.hasOwnProperty.call(map, key)) throw new Error(`Unknown sync entity for ${what}: ${key}`);
  return map[key] as T;
}

/**
 * Validates `fields` of a push `upsert` that patches an existing row — the same Zod as the web PATCH
 * (`partialOf(create schema)` per module, `updateWeddingSchema`), so an absent key stays absent and unknown keys are
 * stripped. `membership` → `syncMembershipPatchSchema` (strict); `activityLog` (import only) →
 * `syncActivityImportSchema`.
 */
export function syncEntityFieldsSchema(entity: SyncEntity): z.ZodType {
  return own(SYNC_PATCH_SCHEMAS, entity, "fields");
}

/**
 * Validates `fields` when the row does not exist yet (push `upsert` of a new id, import of a new row): the web POST
 * schema, defaults filled. `inspirationPhoto` has no JSON create — its row is created by the multipart upload; the
 * schema only covers its metadata.
 */
export function syncEntityCreateSchema(entity: SyncImportEntity): z.ZodType {
  return own(SYNC_CREATE_SCHEMAS, entity, "create");
}

/* ───────────────────────── Request bodies ───────────────────────── */

/** `POST /weddings/:weddingId/sync/pull`. `cursor` 0 = from the start (also what a device sends after `fullResync`). */
export const syncPullBodySchema = z.object({
  cursor: z.number().int().min(0),
  limit: z.number().int().min(1).max(SYNC_LIMITS.pullMax).default(SYNC_LIMITS.pullDefault),
});
export type SyncPull = z.infer<typeof syncPullBodySchema>;
export type SyncPullBody = z.input<typeof syncPullBodySchema>;

const pushChangeBase = { changeId: syncChangeIdSchema, entity: z.enum(SYNC_PUSH_ENTITY), id: syncIdSchema };

/**
 * One pushed change. `upsert` requires `fields` (a patch of changed fields only; `{}` is allowed and restores a
 * soft-deleted row); `delete` carries none (a sent `fields` is stripped). Fields are validated per change by the
 * server with `syncEntityFieldsSchema` / `syncEntityCreateSchema`, so one bad change never fails the batch.
 */
export const syncPushChangeSchema = z.discriminatedUnion("op", [
  z.object({ ...pushChangeBase, op: z.literal("upsert"), fields: z.record(z.string(), z.unknown()) }),
  z.object({ ...pushChangeBase, op: z.literal("delete") }),
]);
export type SyncPushChange = z.infer<typeof syncPushChangeSchema>;

/** `POST /weddings/:weddingId/sync/push`. Changes apply in array order; a repeated `changeId` is answered as a replay. */
export const syncPushBodySchema = z.object({
  changes: z.array(syncPushChangeSchema).min(1).max(SYNC_LIMITS.pushMax),
});
export type SyncPush = z.infer<typeof syncPushBodySchema>;
export type SyncPushBody = z.input<typeof syncPushBodySchema>;

export const SYNC_IMPORT_ORIGIN = ["cloudkit", "device"] as const;
export type SyncImportOrigin = (typeof SYNC_IMPORT_ORIGIN)[number];

export const SYNC_IMPORT_STATUS = ["in_progress", "completed"] as const;
export type SyncImportStatus = (typeof SYNC_IMPORT_STATUS)[number];

/**
 * The wedding an import creates (with the device's id) when it is absent: the `POST /weddings` fields minus
 * `seedDefaults` (an import is never seeded — the device sends its seeds as rows) plus the two settings the device
 * also holds. Currency is resolved like Apple's `Money.resolved` (unknown → "USD") so an old stored code never blocks
 * a move.
 */
export const syncImportWeddingSchema = createWeddingSchema.omit({ seedDefaults: true }).extend({
  id: syncIdSchema,
  currencyCode: z.string().transform((c) => resolveCurrency(c)).default("USD"),
  rsvpDeadline: isoDateNullable.default(null),
  categoryBudgets: categoryBudgetsSchema.default({}),
  /** The device's creation time; absent = now. */
  createdAt: isoDate.optional(),
});
export type SyncImportWedding = z.infer<typeof syncImportWeddingSchema>;

/**
 * One imported row. `fields` is validated per row with `syncEntityCreateSchema(entity)` after `normalizeSyncIdFields`;
 * a bad row is reported in `rejected`, never dropped silently. An `inspirationPhoto` row only sets the metadata of a
 * photo already uploaded with the device's id (else `UNSUPPORTED_ENTITY`, sent again after the upload).
 */
export const syncImportRowSchema = z.object({
  entity: z.enum(SYNC_IMPORT_ENTITY),
  id: syncIdSchema,
  fields: z.record(z.string(), z.unknown()),
  /** A tombstone: imported soft-deleted (restorable for `RETENTION.softDeleteDays`, then purged). */
  deletedAt: isoDateNullable.optional(),
  /** The device's creation time; absent = now. */
  createdAt: isoDate.optional(),
});
export type SyncImportRow = z.infer<typeof syncImportRowSchema>;

/** `POST /sync/import` — one chunk. Owner only; idempotent (rows upsert by id); `final: true` marks the import complete. */
export const syncImportBodySchema = z.object({
  wedding: syncImportWeddingSchema,
  rows: z.array(syncImportRowSchema).max(SYNC_LIMITS.importChunkMax),
  origin: z.enum(SYNC_IMPORT_ORIGIN),
  final: z.boolean(),
});
export type SyncImport = z.infer<typeof syncImportBodySchema>;
export type SyncImportBody = z.input<typeof syncImportBodySchema>;

/** Coordinator-spec names for the three bodies (same objects). */
export const syncPullSchema = syncPullBodySchema;
export const syncPushSchema = syncPushBodySchema;
export const syncImportSchema = syncImportBodySchema;

/* ───────────────────────── Rejections ───────────────────────── */

const SHARED_REJECT_CODES = [
  "VALIDATION_FAILED", "FORBIDDEN", "NOT_FOUND", "CONFLICT",
  "FREE_LIMIT_REACHED", "PLAN_LIMIT_REACHED", "PLAN_REQUIRED", "INTERNAL",
] as const satisfies readonly ErrorCode[];

/**
 * Codes allowed in `rejected[].code` (push) and import `rejected[]`: the shared API codes plus `UNSUPPORTED_ENTITY`
 * (an entity/op pair the server does not take through sync — see `SYNC_ENTITY_OPS`). Whole-request failures keep the
 * normal error envelope (401, 403 revoked/not a member, 404, 413, 429).
 */
export const SYNC_REJECT_CODES = [...SHARED_REJECT_CODES, "UNSUPPORTED_ENTITY"] as const;
export type SyncRejectCode = (typeof SYNC_REJECT_CODES)[number];

/**
 * What a device does with a rejected change. It is always shown to the user (toast / upgrade prompt), never dropped
 * silently:
 * - `revert` — the server is right (access is fail-closed): drop the queued change and restore the row from the
 *   server's copy (pull it if needed). FORBIDDEN, NOT_FOUND, CONFLICT.
 * - `hold` — keep the change and the local value, retry after the plan, the row or the app/server version changes:
 *   caps, unknown entity/op, and VALIDATION_FAILED (Apple has no web text/range limits, and a newer client may send
 *   an enum raw value an older server doesn't know yet — reverting would destroy the user's edit).
 * - `retry` — transient; retry with backoff, same `changeId`.
 */
export const SYNC_REJECT_ACTION = ["revert", "hold", "retry"] as const;
export type SyncRejectAction = (typeof SYNC_REJECT_ACTION)[number];

export const SYNC_REJECT_CODE_ACTION: Readonly<Record<SyncRejectCode, SyncRejectAction>> = {
  VALIDATION_FAILED: "hold",
  FORBIDDEN: "revert",
  NOT_FOUND: "revert",
  CONFLICT: "revert",
  FREE_LIMIT_REACHED: "hold",
  PLAN_LIMIT_REACHED: "hold",
  PLAN_REQUIRED: "hold",
  UNSUPPORTED_ENTITY: "hold",
  INTERNAL: "retry",
};

/** Unknown codes (a newer server) are held, never dropped: user data is not discarded on a code we don't know. */
export function syncRejectAction(code: string): SyncRejectAction {
  return Object.prototype.hasOwnProperty.call(SYNC_REJECT_CODE_ACTION, code)
    ? SYNC_REJECT_CODE_ACTION[code as SyncRejectCode]
    : "hold";
}

/* ───────────────────────── Response DTOs ───────────────────────── */

/**
 * A live row. `data` is the entity's web DTO (GuestDTO, WeddingDTO, ActivityDTO, …; `SyncMembershipDTO` for
 * memberships), money `null` when masked. Devices ignore unknown keys and unknown entities (a newer server), keep an
 * unknown enum raw value as received, and push a field only when the user changed it — so neither is overwritten.
 */
export interface SyncChangeDTO {
  entity: SyncEntity;
  id: string;
  serverSeq: number;
  data: Record<string, unknown>;
}

/** A soft-deleted row (tombstone). */
export interface SyncDeleteDTO {
  entity: SyncEntity;
  id: string;
  serverSeq: number;
}

/**
 * One pull page, ordered by `serverSeq`. Next request sends `cursor`; repeat while `hasMore`. `fullResync: true`
 * (the device's cursor predates the wedding's last hard purge or its own access widened) means the pages from cursor
 * 0 are the complete live set: after the last page the device removes local rows of that wedding it did not receive
 * (keeping rows with unsent local changes).
 */
export interface SyncPullDTO {
  changes: SyncChangeDTO[];
  deletes: SyncDeleteDTO[];
  cursor: number;
  hasMore: boolean;
  fullResync: boolean;
}

export interface SyncAppliedDTO {
  changeId: string;
  /** The row's `server_seq` after this change (a replay returns the stored value). */
  serverSeq: number;
}

export interface SyncRejectedDTO {
  changeId: string;
  code: SyncRejectCode;
  message: string;
}

/** `cursor` = the wedding's `change_seq` after the batch: a hint to pull, never a replacement for the pull cursor. */
export interface SyncPushResultDTO {
  applied: SyncAppliedDTO[];
  rejected: SyncRejectedDTO[];
  cursor: number;
}

export interface SyncImportRejectedDTO {
  entity: SyncImportEntity;
  id: string;
  code: SyncRejectCode;
  message: string;
}

/** `counts` = rows applied per `SyncImportEntity` over the whole import so far; `rejected` = rows of this chunk not applied. */
export interface SyncImportResultDTO {
  weddingId: string;
  status: SyncImportStatus;
  counts: Record<string, number>;
  rejected: SyncImportRejectedDTO[];
}

/** `GET /sync/weddings` — every server wedding the caller belongs to, plus where its change counter stands. */
export interface SyncWeddingSummaryDTO extends WeddingSummaryDTO {
  changeSeq: number;
}

/**
 * `data` of a `membership` change: the GET /members row (e-mail `null` unless the caller can manage people) plus the
 * Apple identity and the invite contact fields (empty unless the caller can manage people).
 */
export interface SyncMembershipDTO extends MemberDTO {
  identity: string;
  inviteEmail: string;
  invitePhone: string;
}
